import React from 'react';

const getApi = async () => {
    const API = (await import('../../../data/api')).default;
    return API.Instance();
};

/**
 * Data + selection state for CalendarStoresModal: loads stores (paginated, with search), the
 * client / tag filters, the tag's store ids, and keeps the selected store ids for the day.
 */
export function useCalendarStoresModal({ isOpen, calendarStores, stores }) {
    const storeList = calendarStores?.map((store) => store._id);
    const [loading, setLoading] = React.useState(false);
    const [search, setSearch] = React.useState('');
    const [currentPage, setCurrentPage] = React.useState(1);
    const [totalStores, setTotalStores] = React.useState(0);
    const [totalPages, setTotalPages] = React.useState(0);
    const [displayedStores, setDisplayedStores] = React.useState([]);
    const [selectedStores, setSelectedStores] = React.useState(storeList);
    const [searchResults, setSearchResults] = React.useState([]);
    const [isSearching, setIsSearching] = React.useState(false);
    const [clients, setClients] = React.useState([]);
    const [tags, setTags] = React.useState([]);
    const [selectedClientId, setSelectedClientId] = React.useState('');
    const [selectedTagId, setSelectedTagId] = React.useState('');
    const [tagStoreIds, setTagStoreIds] = React.useState(null);
    const [loadingFilters, setLoadingFilters] = React.useState(false);
    const [loadingTagItems, setLoadingTagItems] = React.useState(false);

    React.useEffect(() => {
        setSelectedStores(storeList);
        if (isOpen) {
            loadStoresPage(1);
            loadFilters();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    React.useEffect(() => {
        if (isOpen) {
            loadStoresPage(currentPage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage]);

    React.useEffect(() => {
        if (!isOpen) return;
        if (!selectedTagId) {
            setTagStoreIds(null);
            return;
        }
        let isActive = true;
        const loadTagItems = async () => {
            setLoadingTagItems(true);
            try {
                const api = await getApi();
                const response = await api.requestTagItems(selectedTagId);
                const items = response?.items || [];
                const storeIds = items.filter((item) => item?.modelType === 'Store').map((item) => item?.modelId?.toString());
                if (isActive) {
                    setTagStoreIds(new Set(storeIds.filter(Boolean)));
                }
            } catch (error) {
                console.error('Error loading tag items:', error);
                if (isActive) {
                    setTagStoreIds(new Set());
                }
            } finally {
                if (isActive) {
                    setLoadingTagItems(false);
                }
            }
        };
        loadTagItems();
        return () => {
            isActive = false;
        };
    }, [selectedTagId, isOpen]);

    const loadFilters = async () => {
        setLoadingFilters(true);
        try {
            const api = await getApi();
            const [clientsResponse, tagsResponse] = await Promise.all([api.requestClients(), api.requestTags({ modelType: 'Store' })]);

            const clientList = Array.isArray(clientsResponse) ? clientsResponse : clientsResponse?.clients || clientsResponse?.data || [];
            const tagList = tagsResponse?.tags || tagsResponse?.data || [];

            setClients(clientList || []);
            setTags(tagList || []);
        } catch (error) {
            console.error('Error loading filters:', error);
            setClients([]);
            setTags([]);
        } finally {
            setLoadingFilters(false);
        }
    };

    const loadStoresPage = async (page = 1) => {
        setLoading(true);
        try {
            const api = await getApi();
            const response = await api.requestStores(page, 1000, search);

            if (response && Array.isArray(response)) {
                // Old response format (backward compatibility)
                setDisplayedStores(response);
                setTotalStores(response.length);
                setTotalPages(1);
            } else if (response && response.stores) {
                // Paginated response format
                setDisplayedStores(response.stores);
                setTotalStores(response.pagination?.total || response.stores.length);
                setTotalPages(response.pagination?.pages || 1);
            } else {
                // Fallback to passed stores prop
                setDisplayedStores(stores || []);
                setTotalStores(stores?.length || 0);
                setTotalPages(1);
            }
        } catch (error) {
            console.error('Error loading stores:', error);
            // Fallback to passed stores prop
            setDisplayedStores(stores || []);
            setTotalStores(stores?.length || 0);
            setTotalPages(1);
        } finally {
            setLoading(false);
        }
    };

    const onSearch = async (value) => {
        setSearch(value);
        setIsSearching(true);

        if (value.trim() === '') {
            setSearchResults([]);
            setCurrentPage(1);
            loadStoresPage(1);
        } else {
            try {
                const api = await getApi();
                const response = await api.requestStores(1, 1000, value);

                if (response && Array.isArray(response)) {
                    setSearchResults(response);
                } else if (response && response.stores) {
                    setSearchResults(response.stores);
                } else {
                    setSearchResults([]);
                }
            } catch (error) {
                console.error('Error searching stores:', error);
                setSearchResults([]);
            }
        }
        setIsSearching(false);
    };

    /** Adds the store to the day's selection, or removes it when already selected. */
    const toggleStore = (storeId) => {
        if (selectedStores?.includes(storeId)) {
            setSelectedStores(selectedStores?.filter((id) => id !== storeId));
        } else {
            setSelectedStores([...selectedStores, storeId]);
        }
    };

    const fullStores = Array.isArray(stores) && stores.length > 0 ? stores : displayedStores;
    const finalDisplayedStores = search !== '' ? searchResults : fullStores;
    const filteredStores = React.useMemo(() => {
        let data = finalDisplayedStores || [];

        if (selectedClientId) {
            const selectedClient = clients.find((client) => client?._id?.toString() === selectedClientId);
            const selectedClientName = selectedClient?.name;
            data = data.filter((store) => {
                const storeClientId = store?.client?._id?.toString() || store?.client?.toString() || store?.clientId?.toString();
                if (storeClientId && storeClientId === selectedClientId) return true;
                if (selectedClientName && store?.clientName === selectedClientName) return true;
                return false;
            });
        }

        if (selectedTagId) {
            if (!tagStoreIds) return [];
            data = data.filter((store) => tagStoreIds.has(store?._id?.toString()));
        }

        return data;
    }, [clients, finalDisplayedStores, selectedClientId, selectedTagId, tagStoreIds]);

    return {
        loading,
        search,
        currentPage,
        setCurrentPage,
        totalStores,
        totalPages,
        selectedStores,
        toggleStore,
        isSearching,
        onSearch,
        clients,
        tags,
        selectedClientId,
        setSelectedClientId,
        selectedTagId,
        setSelectedTagId,
        loadingFilters,
        loadingTagItems,
        filteredStores
    };
}
